import type { CoverageState } from '../api/types'

/** Percentages render to 2 dp (§10). Edit fields use 6 dp; display never does. */
export function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`
}

export function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/*
  Semantic colour is never decorative here: ok means the account is fully routed,
  error means it is routed nowhere and QC-A001 will block a run against it, warn means
  the percentages do not total 100 and QC-A002 will. NotAllocable is neutral because
  it is not a problem — it is a deliberate exclusion.
*/
const coverageStyles: Record<CoverageState, { bg: string; fg: string; label: string }> = {
  Complete: { bg: 'var(--color-ok-wash)', fg: 'var(--color-ok)', label: 'Complete' },
  Partial: { bg: 'var(--color-warn-wash)', fg: 'var(--color-warn)', label: 'Partial' },
  None: { bg: 'var(--color-error-wash)', fg: 'var(--color-error)', label: 'None' },
  NotAllocable: {
    bg: 'var(--color-panel-alt)',
    fg: 'var(--color-muted)',
    label: 'Not allocable',
  },
}

/**
 * The chart-of-accounts coverage badge (§10 screen 2): green at 100%, amber showing
 * the actual percentage when partial, red at zero, grey when the account is not
 * allocable at all.
 *
 * Partial shows the number rather than just the colour, because "amber" tells an
 * analyst something is wrong and the percentage tells them how wrong.
 */
export function CoverageBadge({
  state,
  percent,
}: {
  state: CoverageState
  percent: number
}) {
  const style = coverageStyles[state]
  const showPercent = state === 'Partial'

  return (
    <span
      className="badge"
      style={{ background: style.bg, color: style.fg }}
      title={
        state === 'NotAllocable'
          ? 'This account does not participate in allocation.'
          : `Driver percentages total ${percent.toFixed(6)}%`
      }
    >
      {style.label}
      {showPercent && <span className="num tabular-nums">{formatPercent(percent)}</span>}
    </span>
  )
}

/**
 * Per-node coverage badge for the tree (§10 screen 1): how many drivers target this cost
 * center, and nothing else. Red only when that number is zero on a node that was supposed
 * to receive something.
 *
 * @remarks
 * IT USED TO CARRY A SECOND FIGURE, and both attempts at one were wrong.
 *
 * First the SUM OF THE DRIVERS' PERCENTAGES, which is not a quantity. Every driver's
 * shares total 100 across the cost centers IT names, so adding one node's shares across
 * drivers adds fractions with unrelated denominators: 59.885932% of Square Feet plus
 * 58.757436% of Direct Nursing Hours plus 40.368509% of Costed Requisitions badged
 * 159.01%, which is not 159% of anything. It said nothing about how much cost lands here
 * either — that depends on the balances routed through each driver, and ten dollars
 * through one and ten million through another give the same number. It also read as an
 * error, because a percentage over 100 does.
 *
 * Then the last completed run's allocated amount, which IS a real number but is not this
 * badge's business: it belongs to a RUN, and this column is about CONFIGURATION. It also
 * says nothing on an installation that has not run yet, which is exactly when somebody is
 * building the tree and reading this column most.
 *
 * So the badge answers one question and answers it completely: how many drivers name this
 * node, and the red state when the answer is none. What each of them contributes is a
 * per-driver figure with a denominator of its own, and it lives on the driver editor and
 * the configuration overview, where there is room to attach it to the driver it belongs
 * to.
 *
 * QC-C009 still sums the percentages and is still right to. It uses the total only as a
 * FLOOR TEST — below 0.5% across everything means every individual share is below 0.5%,
 * since none can be negative — and a conservative bound is a legitimate use of a figure
 * that is meaningless as a quantity. Reporting a bound is not the same as displaying it.
 */
export function NodeCoverageBadge({
  driverCount,
  isAllocable,
}: {
  driverCount: number
  isAllocable: boolean
}) {
  /*
      "No coverage" is only a fault on a node that was supposed to receive something.

      A cost center marked NOT ALLOCABLE is a heading — an explicit opt-out (D8), and the
      one thing that makes a node off-limits. Nothing is meant to post to it, so nothing
      targeting it is the configuration working. This badge painted it red anyway, which
      put a red 'no coverage' on the same row as the grey 'not allocable' flag that
      explains why: the row contradicted itself, and the contradiction was in the colour
      that means "stop".

      QC-C006 has always scoped its finding to allocable nodes. This is the tree catching
      up with the rule it was reporting.
  */
  const none = driverCount === 0
  const fault = none && isAllocable

  return (
    <span
      className="badge"
      style={{
        background: fault ? 'var(--color-error-wash)' : 'var(--color-panel-alt)',
        color: fault ? 'var(--color-error)' : 'var(--color-muted)',
      }}
      title={
        fault
          ? 'No driver targets this cost center. It will receive nothing, and every other check can still pass.'
          : none
            ? 'Not allocable, so nothing is meant to post to it directly. Cost reaches it through its children, and the rollup shows it.'
            : `${driverCount} driver${driverCount === 1 ? '' : 's'} target this cost center. ` +
              'What each one contributes is on the driver, and on the configuration overview.'
      }
    >
      {/*
          Spelled out, not abbreviated.

          It was `3d`, and `d` was read as DAYS — which is not a stretch in a product that
          also reports patient days and cost per patient day. Two characters saved is not
          worth a column that means something else to the reader than to the writer.
      */}
      {/*
          A non-allocable node with no drivers gets an em dash rather than a count or a
          verdict. 'not allocable' is already on the row, in the flags column where it
          belongs; repeating it here would be the same fact twice, and saying anything
          stronger would be saying it wrongly.
      */}
      {fault
        ? 'no coverage'
        : none
          ? '—'
          : `${driverCount} driver${driverCount === 1 ? '' : 's'}`}
    </span>
  )
}

/**
 * Marks a node created since the last completed run — the QC-C005 condition.
 *
 * Worth surfacing in the tree because this is the failure every other rule misses:
 * each driver still totals 100%, every account is still fully mapped, the run
 * reconciles perfectly, and the new department is absent from every report.
 */
export function NewNodeBadge() {
  return (
    <span
      className="badge"
      style={{ background: 'var(--color-accent-wash)', color: 'var(--color-accent)' }}
      title="Created since the last completed run. It will not appear in any report until a driver targets it (QC-C005)."
    >
      new
    </span>
  )
}
